import { CommonModule } from '@angular/common';
import { Component, DoCheck, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { WarehousingMasterData, WarehousingWorkDirectoryRow } from '../warehousing.model';
import { WarehousingPageHeaderComponent } from '../warehousing-page-header.component';
import { WarehousingService } from '../warehousing.service';

type WorkState = 'ALL' | 'INBOUND' | 'EXCEPTION' | 'TRANSFER' | 'DISPATCH' | 'RETURN';

@Component({
  selector: 'app-warehousing-work-directory',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ButtonModule, InputTextModule, ProgressBarModule,
    SelectModule, TableModule, TagModule, WarehousingPageHeaderComponent],
  templateUrl: './warehousing-work-directory.component.html',
  styleUrl: './warehousing-work-directory.component.css'
})
export class WarehousingWorkDirectoryComponent implements OnInit, DoCheck {
  private service = inject(WarehousingService);
  private session = inject(SessionStorageService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messages = inject(MessageService);
  private permissions = inject(PermissionService);
  private companyId = 0;
  warehouses: WarehousingMasterData[] = [];
  warehouseId?: number;
  direction: 'INBOUND' | 'OUTBOUND' = 'OUTBOUND';
  type: 'SHIPMENT' | 'DELIVERY' = 'SHIPMENT';
  state: WorkState = 'ALL';
  get states(): Array<{ label: string; value: WorkState }> {
    return this.direction === 'INBOUND'
      ? [{ label: 'All inbound work', value: 'ALL' }, { label: 'To receive', value: 'INBOUND' },
          { label: 'Returns', value: 'RETURN' }, { label: 'Exceptions', value: 'EXCEPTION' }]
      : [{ label: 'All outbound work', value: 'ALL' }, { label: 'Transfers to send', value: 'TRANSFER' },
          { label: 'Dispatches to send', value: 'DISPATCH' }];
  }
  searchText = '';
  rows: WarehousingWorkDirectoryRow[] = [];
  total = 0;
  page = 0;
  size = 25;
  loading = false;
  queueError?: string;
  private requestId = 0;

  get warehouseName(): string { return this.warehouses.find(w => w.id === this.warehouseId)?.name || ''; }
  canViewInbound(): boolean { return this.permissions.hasPermission(app_permissions.warehousing.transfer.view)
    || this.permissions.hasPermission(app_permissions.warehousing.stock_entry.view); }

  ngOnInit(): void {
    this.companyId = this.service.getCompanyId();
    this.direction = this.route.snapshot.data['direction'] === 'INBOUND' ? 'INBOUND' : 'OUTBOUND';
    const params = this.route.snapshot.queryParamMap;
    const legacyNumber = params.get('deliveryNumber') || params.get('shipmentNumber');
    if (legacyNumber) {
      this.service.getShipmentWork(legacyNumber).subscribe({
        next: response => {
          const work = response.data;
          const type = work.selectedDeliveryId ? 'DELIVERY' : 'SHIPMENT';
          const id = work.selectedDeliveryId || work.shipmentId;
          const warehouseId = Number(params.get('warehouseId') || this.readWarehouse());
          const openRows = (work.rows || []).filter(row => !['RECEIVED', 'DISPATCHED', 'CANCELLED'].includes(row.status || ''));
          const hasInbound = openRows.some(row => row.documentType === 'TRANSFER'
            && row.destinationWarehouseId === warehouseId
            && ['IN_TRANSIT', 'PARTIALLY_RECEIVED'].includes(row.status || ''));
          const hasOutbound = openRows.some(row => row.sourceWarehouseId === warehouseId
            && (row.documentType === 'DISPATCH' || !['IN_TRANSIT', 'PARTIALLY_RECEIVED'].includes(row.status || '')));
          const direction = hasInbound && !hasOutbound ? 'INBOUND' : this.direction;
          if (id) this.router.navigate(['/warehousing/shipment-work', type, id],
            { queryParams: { warehouseId: params.get('warehouseId') || this.readWarehouse(), tab: type,
              direction }, replaceUrl: true });
        },
        error: () => this.messages.add({ severity: 'error', summary: 'Work not found', detail: legacyNumber })
      });
    }
    const requestedWarehouse = Number(params.get('warehouseId') || this.readWarehouse());
    if (requestedWarehouse) this.warehouseId = requestedWarehouse;
    this.type = params.get('tab') === 'DELIVERY' ? 'DELIVERY' : 'SHIPMENT';
    const requestedCompany = this.companyId;
    this.service.getMasterData('WAREHOUSE').subscribe({
      next: response => {
        if (this.companyId !== requestedCompany) return;
        this.warehouses = (response.data || []).filter(w => w.isActive !== false);
        if (!this.warehouses.some(w => w.id === this.warehouseId)) this.warehouseId = undefined;
        if (this.warehouseId) this.refresh();
      },
      error: () => this.messages.add({ severity: 'error', summary: 'Warehouses unavailable', detail: 'Refresh to try again.' })
    });
  }

  ngDoCheck(): void {
    const current = this.service.getCompanyId();
    if (current === this.companyId) return;
    this.companyId = current;
    this.warehouseId = Number(this.readWarehouse()) || undefined;
    this.rows = [];
    this.total = 0;
    const requestedCompany = current;
    this.service.getMasterData('WAREHOUSE').subscribe({ next: response => {
      if (this.companyId !== requestedCompany) return;
      this.warehouses = (response.data || []).filter(w => w.isActive !== false);
      if (!this.warehouses.some(w => w.id === this.warehouseId)) this.warehouseId = undefined;
      this.refresh(true);
    } });
  }

  warehouseChanged(): void {
    if (this.warehouseId) localStorage.setItem(this.storageKey(), String(this.warehouseId));
    this.refresh(true);
  }
  setType(type: 'SHIPMENT' | 'DELIVERY'): void { this.type = type; this.refresh(true); }
  loadPage(event: TableLazyLoadEvent): void {
    const newSize = event.rows || this.size;
    const newPage = Math.floor((event.first || 0) / newSize);
    if (newPage === this.page && newSize === this.size) return;
    this.page = newPage;
    this.size = newSize;
    this.refresh();
  }
  refresh(resetPage = false): void {
    if (resetPage) this.page = 0;
    const requestId = ++this.requestId;
    this.queueError = undefined;
    this.router.navigate([], { relativeTo: this.route, queryParams: { warehouseId: this.warehouseId || null,
      tab: this.type }, queryParamsHandling: 'merge', replaceUrl: true });
    if (!this.warehouseId) { this.rows = []; this.total = 0; this.loading = false; return; }
    const requestedCompany = this.companyId;
    const requestedWarehouse = this.warehouseId;
    const requestedType = this.type;
    const requestedDirection = this.direction;
    this.loading = true;
    this.service.getShipmentWorkDirectory(this.warehouseId, this.type, this.direction, this.searchText.trim(), this.state,
      this.page, this.size).subscribe({
        next: response => {
          if (requestId !== this.requestId || this.companyId !== requestedCompany || this.warehouseId !== requestedWarehouse ||
              this.type !== requestedType || this.direction !== requestedDirection) return;
          this.rows = response.data?.content || [];
          this.total = response.data?.totalElements || 0;
          this.loading = false;
        },
        error: () => {
          if (requestId !== this.requestId || this.companyId !== requestedCompany || this.warehouseId !== requestedWarehouse ||
              this.type !== requestedType || this.direction !== requestedDirection) return;
          this.loading = false;
          this.rows = [];
          this.total = 0;
          this.queueError = 'Unable to load pending warehouse work. Please retry.';
          this.messages.add({ severity: 'error', summary: 'Queue unavailable',
            detail: this.queueError });
        }
      });
  }
  detailLink(row: WarehousingWorkDirectoryRow): string[] {
    return ['/warehousing/shipment-work', this.type, String(row.id)];
  }
  detailParams(): Record<string, string> {
    return { warehouseId: String(this.warehouseId), tab: this.type, direction: this.direction };
  }
  queueLink(direction: 'INBOUND' | 'OUTBOUND'): string[] {
    return ['/warehousing/shipment-work', direction.toLowerCase()];
  }
  queueParams(): Record<string, string | null> {
    return { warehouseId: this.warehouseId ? String(this.warehouseId) : null, tab: this.type };
  }
  private storageKey(): string {
    const user = this.session.getUser();
    return `warehouse-work-location:${this.companyId}:${user?.id || user?.uid || 0}`;
  }
  private readWarehouse(): string { return localStorage.getItem(this.storageKey()) || ''; }
}
